<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Uwagi do maszyn</span>
        <h3 class="page-title">Lista uwag do maszyn</h3>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-0 page-filters">
      <validation-observer
        ref="machines-comments-filters-form"
        tag="form"
        class="d-flex mb-2">
        <div
          :class="{
            'form-control': true,
            'is-select': true,
            'vs-wrap-text': true
          }"
          class="mr-1 mb-2"
          style="min-width: 220px;">
          <v-select
            :options="allMachines"
            v-model="filters.machine_id"
            :reduce="(machine) => machine.id"
            placeholder="Filtruj po maszynie"
            label="name"
            :clearable="true">
          </v-select>
        </div>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-md-auto mr-1 mb-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap mb-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się.
      <a
        href="javascript:window.location.reload();"
        class="alert-link">
        Odśwież stronę
      </a>
      aby spróbować ponownie.
    </d-alert>
    <d-card
      v-else
      style="width: 100%; min-height: 100px;"
      class="position-relative">
      <transition name="fade">
        <div
          v-if="!loaded"
          class="overlay-loading" >
          <div class="loading-spinner pb-3 pt-3 text-center">
            <span class="loading-spinner-content text-primary">
              Trwa ładowanie danych&hellip;
            </span>
          </div>
        </div>
      </transition>
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0 align-middle">Maszyna / Zamówienie</th>
            <th
              scope="col"
              class="border-0 align-middle">Uwagi</th>
            <th
              scope="col"
              class="border-0 align-middle text-right">Zgłoszenie</th>
            <th
              scope="col"
              class="border-0 align-middle text-center">Notatki</th>
            <th
              scope="col"
              class="border-0 align-middle text-center"
              style="width: 100px;">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(item, index) of listItems"
            :key="'item-' + index">
            <td class="align-middle py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Maszyna / Zamówienie</label>
              <div
                class="d-flex flex-column"
                style="gap: 5px;">
                <span class="text-primary">{{ item.machine_name || item.machine_id }}</span>
                <small><span class="text-secondary">Zamówienie: </span><strong class="text-dark">{{ item.order_name || item.order_id }}</strong></small>
                <small><span class="text-secondary">Zdobienie: </span><strong class="text-dark">{{ item.order_service_name || item.order_service_id }}</strong></small>
              </div>
            </td>
            <td
              class="align-middle py-lg-1 py-md-3 py-3"
              style="max-width: 300px;">
              <label class="table-mobile-label mb-1">Uwagi</label>
              <span
                class="d-inline-block text-truncate w-100"
                :title="item.comment">{{ item.comment }}</span>
            </td>
            <td class="align-middle text-right py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Zgłoszenie</label>
              <span class="d-block">{{ item.user_name }}</span>
              <small class="text-muted">{{ formatDateTimeString(item.created_at) }}</small>
            </td>
            <td class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Notatki</label>
              <d-badge :theme="item.notes && item.notes.length ? 'info' : 'light'">
                {{ item.notes.length || 0 }}
              </d-badge>
            </td>
            <td class="text-center align-middle pt-0">
              <d-button
                theme="primary"
                @click.native="$router.push('/maszyny/uwagi/edycja/' + item.id)"
                class="mx-2 mt-2"
                title="Szczegóły">
                <i class="material-icons">visibility</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="listItems.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak uwag do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import Pagination from '@/components/common/Pagination.vue';

export default {
  name: 'machines-comments',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data () {
    return {
      listItems: [],
      loaded: false,
      loadError: false,
      allMachines: [],
      loadedMachines: false,
      filters: {
        machine_id: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadMachines();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.machine_id) {
        where.machine_id = this.filters.machine_id;
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/machines/comments/items?page=' + this.currentPage,
        listField: 'listItems',
        pagination: true,
        params: {
          where
        }
      });
    },
    loadMachines () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/machines/items',
        method: 'get',
        outputKey: 'allMachines',
        loadedKey: 'loadedMachines',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        machine_id: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    formatDateTimeString (date) {
      return CalendarUtils.formatDateTimeString(date);
    }
  }
};

</script>

<style lang="scss">
.page-filters form {
  width: 100%;
}

@media screen  and (max-width: 1230px) {
  .page-filters {
    form {
      flex-wrap: wrap;

      input {
        margin-right: 0!important;
        min-width: unset!important;
        width: 100%;
      }
    }
  }
}

@media screen  and (max-width: 768px) {
  .page-filters {
    form {
      flex-wrap: wrap;

      .v-select {
        margin-right: 0!important;
        max-width: unset!important;
        width: 100%!important;
      }
    }
  }
}

</style>
